import { useAsyncData, useAsyncMutation } from './useAsyncData';
import {
  getCompanyMetrics,
  getEmployees,
  getEmployeeById,
  getEmployeeProgress,
  getDepartmentStats,
  exportReport,
  setEmployeeActive,
} from '@/services/api/company.api';
import type {
  CompanyMetrics,
  Employee,
  EmployeeFilters,
  EmployeeProgress,
  DepartmentStats,
  ExportReportRequest,
  ExportReportResponse,
} from '@/types/company.types';

export function useCompanyMetrics() {
  const { data: metrics, isLoading, error } = useAsyncData<CompanyMetrics>({
    fetchFn: getCompanyMetrics,
  });
  return { metrics, isLoading, error };
}

/**
 * `companyId` solo lo usa backoffice para mirar una empresa concreta.
 * `refreshKey` fuerza un refetch sin cambiar los filtros (p. ej. tras un alta).
 */
export function useEmployees(
  filters?: EmployeeFilters,
  companyId?: string,
  refreshKey?: number
) {
  const { data, isLoading, error } = useAsyncData<{ employees: Employee[]; total: number }>({
    fetchFn: () => getEmployees(filters, companyId),
    dependencies: [filters, companyId, refreshKey],
  });
  return { employees: data?.employees ?? [], total: data?.total ?? 0, isLoading, error };
}

export function useEmployee(employeeId: string | undefined) {
  const { data: employee, isLoading, error } = useAsyncData<Employee | null>({
    fetchFn: () => getEmployeeById(employeeId!),
    dependencies: [employeeId],
    enabled: !!employeeId,
  });
  return { employee, isLoading, error };
}

export function useSetEmployeeActive() {
  const { mutate, isLoading: isSettingActive, error } = useAsyncMutation<
    { userId: string; isActive: boolean },
    { employeeId: string; isActive: boolean }
  >({
    mutationFn: ({ employeeId, isActive }) => setEmployeeActive(employeeId, isActive),
  });
  return {
    setEmployeeActive: (employeeId: string, isActive: boolean) =>
      mutate({ employeeId, isActive }),
    isSettingActive,
    error,
  };
}

export function useEmployeeProgress(employeeId: string | undefined) {
  const { data: progress, isLoading, error } = useAsyncData<EmployeeProgress | null>({
    fetchFn: () => getEmployeeProgress(employeeId!),
    dependencies: [employeeId],
    enabled: !!employeeId,
  });
  return { progress, isLoading, error };
}

export function useDepartmentStats() {
  const { data, isLoading, error } = useAsyncData<DepartmentStats[]>({
    fetchFn: getDepartmentStats,
  });
  return { stats: data ?? [], isLoading, error };
}

export function useExportReport() {
  const { mutate: exportReportData, isLoading: isExporting, error } = useAsyncMutation<
    ExportReportResponse,
    ExportReportRequest
  >({
    mutationFn: exportReport,
  });
  return { exportReportData, isExporting, error };
}
